import React from 'react';
import { DropdownProps } from 'semantic-ui-react';
import { decodeEntities, formatTimestamp } from '../../utils';

const ChatVideoCard: React.FC<{
  video: PlaylistVideo;
  index: number;
  controls?: boolean;
  onPlay?: (index: number) => void;
  onRemove?: (index: number) => void;
  onPlayNext?: (index: number) => void;
  onSetMedia?: (e: any, data: DropdownProps) => void;
  onPlaylistAdd?: (e: any, data: DropdownProps) => void;
  disabled?: boolean;
}> = (props) => {
  const {
    video,
    index,
    controls,
    onPlay,
    onPlayNext,
    onRemove,
    onSetMedia,
    disabled,
    onPlaylistAdd,
  } = props;

  const handlePlayClick = React.useCallback(() => {
    onPlay?.(index);
  }, [onPlay, index]);

  const handlePlayNextClick = React.useCallback(
    (e: any) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      onPlayNext?.(index);
    },
    [onPlayNext, index]
  );

  const handleRemoveClick = React.useCallback(
    (e: any) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      onRemove?.(index);
    },
    [onRemove, index]
  );

  return (
    <div
      title={video.name}
      className="p-6 max-md:p-3 grid grid-cols-[minmax(0,1fr)_142px] max-md:block items-center gap-1"
      onClick={
        onSetMedia ? (e) => onSetMedia(e, { value: video.url }) : undefined
      }
    >
      {/* LEFT SIDE */}
      <div className="flex items-center gap-1 min-w-0">
        {/* Thumbnail */}
        <div className="w-24 h-14 max-md:w-14 shrink-0 overflow-hidden relative bg-[#272b30] rounded-md">
          {!!video.duration && (
            <div className="absolute z-[1] bottom-0 right-0 py-[2px] px-1 bg-black/95 text-white text-sm pointer-events-none">
              {formatTimestamp(video.duration)}
            </div>
          )}
          {!!video.img && (
            <img
              className="absolute inset-0 size-full object-cover"
              src={video.img}
              alt={video.name}
            />
          )}
        </div>

        {/* Icon + Text */}
        <div className="flex items-center gap-1 min-w-0">
          {video.type === 'youtube' && (
            <i className="ri-youtube-fill text-red-500 text-2xl shrink-0"></i>
          )}
          {video.type === 'file' && (
            <i className="ri-link-m text-white text-2xl shrink-0"></i>
          )}
          {video.type === 'magnet' && (
            <svg
              viewBox="0 0 20 20"
              fill="red"
              height="20"
              width="20"
              className="shrink-0"
            >
              <path
                d="M17.35 0.2h-4.0833359375c-0.9020625 0 -1.633328125 0.731265625 -1.633328125 1.6333359375v8.983328125c-0.000875 1.25734375 -1.36253125 2.0422421875 -2.450984375 1.4128203125 -0.504453125 -0.29171875 -0.8152812500000001 -0.83009375 -0.8156874999999999 -1.4128203125V1.8333359375c0 -0.9020703125 -0.731265625 -1.6333359375 -1.633328125 -1.6333359375H2.6500000000000004c-0.9020625 0 -1.6333359375 0.731265625 -1.6333359375 1.6333359375v8.983328125C1.0166640624999999 15.7780234375 5.038640625 19.8 10 19.8h0.06839843749999999c4.9153125 -0.03675 8.9149375 -4.1129375 8.9149375 -9.0854140625v-8.881250000000001c0 -0.9020703125 -0.7312734375000001 -1.6333359375 -1.6333359375 -1.6333359375Z"
                strokeWidth="0.0781"
              />
            </svg>
          )}

          {/* TEXT (IMPORTANT FIX) */}
          <div className="min-w-0 cursor-pointer text-start">
            <h4 className="text-white truncate hover:opacity-85">
              {decodeEntities(video.name)}
            </h4>
            <h4 className="text-white/60 truncate">{video.channel}</h4>
            {onPlaylistAdd && (
              <button
                className="text-gradient-blue text-sm md:hidden duration-200 hover:opacity-70"
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  onPlaylistAdd(e, { value: video.url });
                }}
              >
                Add To Playlist
              </button>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex items-center justify-center flex-col gap-1 shrink-0">
        {onPlaylistAdd && (
          <button
            className="gradient-btn text-black flex items-center gap-1 rounded-lg py-2 px-4 text-[18px] max-md:hidden duration-200  hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              onPlaylistAdd(e, { value: video.url });
            }}
          >
            Add To Playlist
          </button>
        )}

        {controls && (
          <div className="flex items-center gap-1">
            <button
              title="Play now"
              className="text-green-400 text-2xl duration-150 hover:opacity-55"
              onClick={handlePlayClick}
              disabled={disabled}
            >
              <i className="ri-play-fill"></i>
            </button>
            <button
              title="Play next"
              className="text-white text-2xl duration-150 hover:opacity-55"
              onClick={handlePlayNextClick}
              disabled={disabled}
            >
              <i className="ri-arrow-right-double-line"></i>
            </button>
            <button
              title="Remove"
              className="text-red-500 text-[20px] duration-150 hover:opacity-55"
              onClick={handleRemoveClick}
              disabled={disabled}
            >
              <i className="ri-delete-bin-7-fill"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatVideoCard;
